import {
  Badge,
  Card,
  makeStyles,
  Text,
  tokens,
} from "@fluentui/react-components";
import { fromZonedTime } from "date-fns-tz";
import * as React from "react";
import { useContext } from "react";
import { LayoutContext } from "../../../../context/LayoutContext";
import { $cloud30 } from "../../../../cssjs/colors";
import { getAirportConfig } from "../../../../utils/common/CommonHelperFunctions";
import {
  convertToUTC,
  formatRelativeTime,
} from "../../../../utils/common/DateHelperFunctions";
import { formatDisplayValue } from "../../../../utils/common/FormatHelper";
import { IRunwayInfo } from "../../../../utils/maps/MapConstants";
import { getRSCBadgeStatus } from "../../../../utils/maps/MapHelperFunctions";

export interface RunwayConditionsProps extends IRunwayInfo {
  airportCode: string;
  runwayName: string;
  isCurrent: boolean;
}

const RunwayConditions: React.FC<RunwayConditionsProps> = (props) => {
  const styles = useStyles();

  const { timezone, currentTime } = useContext(LayoutContext);
  const { timeZoneId: airportTimeZone } = getAirportConfig(props.airportCode);

  const isLocalTime = timezone === "LT";
  const formatValidityDate = (date: string) => {
    const dateToFormat = isLocalTime
      ? new Date(date)
      : convertToUTC(fromZonedTime(date, airportTimeZone));

    return formatDisplayValue(formatRelativeTime(dateToFormat, currentTime));
  };

  const displayValidFrom = formatValidityDate(props.validFrom);
  const displayValidTo = formatValidityDate(props.validTo);
  const hasValidity = displayValidFrom !== "—" && displayValidTo !== "—";

  return (
    <section
      className={styles.container}
      style={{ opacity: props.isCurrent ? 1 : 0.8 }}
    >
      <Text size={400} weight="semibold">
        {props.isCurrent ? "Current" : "Prior"}
      </Text>

      <Card className={styles.card} aria-label="runway conditions">
        <div className={styles.headerRow}>
          <Text weight="medium" className="pr-sm-4">
            {props.runwayName}
          </Text>
          {hasValidity && (
            <div className={styles.validityColumn}>
              <Text weight="semibold">Validity</Text>
              <Text>
                {displayValidFrom} - {displayValidTo}
              </Text>
            </div>
          )}
        </div>

        <div className={styles.subHeader}>
          <Text weight="medium">RSC</Text>
          <Text weight="medium">Surface</Text>
        </div>

        {props.segments.map((item) => {
          return (
            <div key={item.id} className={styles.itemRow}>
              <Badge
                color={
                  props.isCurrent
                    ? getRSCBadgeStatus(item.status)
                    : "informative"
                }
                shape="circular"
                size="large"
                appearance={props.isCurrent ? "filled" : "tint"}
              >
                {item.value}
              </Badge>
              <Text>{item.description}</Text>
            </div>
          );
        })}
      </Card>
    </section>
  );
};

export default RunwayConditions;

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    width: "max-content",
    minWidth: "250px",
  },
  card: {
    flexGrow: 1,
    padding: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: $cloud30,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalSNudge,
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    fontWeight: tokens.fontWeightSemibold,
  },
  validityColumn: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS,
    alignItems: "flex-end",
  },
  subHeader: {
    display: "flex",
    gap: tokens.spacingVerticalL,
  },
  itemRow: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalL,
  },
});
